import React, {useEffect, useState} from 'react';
import axios from 'axios';
import download from 'js-file-download';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';

const base = '/api/v2/inventory-document/inventory-count';
const actions = {add: 'Új tétel', update: 'Mennyiség frissítése', skip: 'Kihagyás'};
const headers = () => ({Authorization: `Bearer ${localStorage.getItem('userToken')}`, 'App-Network': localStorage.getItem('networkId')});
const number = value => value == null ? '—' : Number(value).toLocaleString('hu-HU', {maximumFractionDigits: 3});

export default function InventoryCountImport({documentId, status, onImported}) {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [duplicates, setDuplicates] = useState('skip');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const editable = !!documentId && status === 'DRAFT';
  useEffect(() => {
    setPreview(null); setFile(null); setVisible(false); setError(''); setSuccess('');
  }, [documentId, status]);

  const template = async () => {
    setBusy(true); setError('');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_HOST}${base}/import-template`, {headers: headers(), responseType: 'blob'});
      download(response.data, 'leltar-sablon.xlsx');
    } catch (e) { setError('A sablon letöltése nem sikerült. Próbáld újra.'); }
    finally { setBusy(false); }
  };
  const submit = async operation => {
    if (!file || !editable) return;
    setBusy(true); setError(''); setSuccess('');
    const data = new FormData();
    data.append('file', file);
    data.append('duplicates', duplicates);
    if (operation === 'apply') data.append('fingerprint', preview.fingerprint);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_HOST}${base}/${documentId}/import/${operation}`, data, {headers: headers()});
      if (operation === 'preview') setPreview(response.data.data);
      else {
        const result = response.data.data;
        setSuccess(`Import kész: ${result.add} új tétel, ${result.update} frissített tétel, ${result.skip} kihagyott sor. A készlet a leltár lezárásakor változik.`);
        setVisible(false); setPreview(null); setFile(null);
        onImported();
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Az import nem sikerült. Kérj új előnézetet és próbáld újra.');
      if (operation === 'apply') setPreview(null);
    } finally { setBusy(false); }
  };
  return <div className="p-mt-3">
    <Button type="button" label="Excel-sablon letöltése" icon="pi pi-download" className="p-button-outlined p-mr-2" disabled={busy} onClick={template}/>
    <Button type="button" label="Tételek importálása Excelből" icon="pi pi-upload" disabled={!editable || busy} onClick={() => { setVisible(true); setError(''); setSuccess(''); }}/>
    {!documentId && <small className="p-d-block p-mt-2">Az importáláshoz előbb mentsd a leltárbizonylatot és a raktárát.</small>}
    {!visible && error && <p role="alert" className="p-error">{error}</p>}
    {success && <p role="status">{success}</p>}
    <Dialog header="Leltártételek importálása Excelből" visible={visible} style={{width: 'min(1100px, 95vw)'}} modal closable={!busy} closeOnEscape={!busy} onHide={() => { if (!busy) setVisible(false); }}
      footer={<>
        <Button type="button" label="Bezárás" className="p-button-text" disabled={busy} onClick={() => setVisible(false)}/>
        <Button type="button" label="Előnézet ellenőrzése" disabled={!file || busy || !editable} onClick={() => submit('preview')}/>
        <Button type="button" label="Tételek importálása" icon="pi pi-check" className="p-button-success" disabled={busy || !editable || !preview || preview.summary.errors > 0 || (preview.summary.add + preview.summary.update === 0)} onClick={() => submit('apply')}/>
      </>}>
      <p>Az import a bizonylat elmentett raktárára vonatkozik. A számolt mennyiség a tényleges készlet: a nulla nullát jelent, az üres mennyiség kimarad. A készlet csak a leltár lezárásakor változik.</p>
      <label htmlFor="inventory-count-excel">Excel-fájl (.xlsx, legfeljebb 5 MB és 5000 sor)</label>
      <input id="inventory-count-excel" type="file" accept=".xlsx" disabled={busy} className="p-d-block p-my-2" onChange={event => {
        const selected = event.target.files?.[0];
        setPreview(null); setError(''); setFile(null);
        if (!selected) return;
        if (!/\.xlsx$/i.test(selected.name) || selected.size > 5 * 1024 * 1024) { setError('Legfeljebb 5 MB méretű .xlsx fájlt válassz.'); event.target.value = ''; return; }
        setFile(selected);
      }}/>
      {file && <p>Kiválasztott fájl: {file.name}</p>}
      <label htmlFor="inventory-count-duplicates" className="p-d-block p-mt-3 p-mb-2">A bizonylaton már szereplő cikk–tárhely párok</label>
      <Dropdown inputId="inventory-count-duplicates" value={duplicates} disabled={busy} options={[{label: 'Kihagyás', value: 'skip'}, {label: 'Megszámolt mennyiség felülírása', value: 'update'}]} onChange={e => { setDuplicates(e.value); setPreview(null); }}/>
      {busy && <p role="status">Feldolgozás folyamatban…</p>}
      {error && <p role="alert" className="p-error">{error}</p>}
      {preview && <>
        <p><strong>Raktár: {preview.warehouse}</strong></p>
        <p>{preview.summary.add} új tétel · {preview.summary.update} frissítés · {preview.summary.skip} kihagyás · {preview.summary.errors} hibás sor</p>
        {preview.summary.errors > 0 && <p className="p-error">Javítsd a hibás sorokat az Excelben, majd töltsd fel újra. Hibás fájlból egyetlen tétel sem kerül mentésre.</p>}
        <DataTable value={preview.rows} dataKey="row" paginator rows={10} responsiveLayout="scroll" emptyMessage="Nincs tétel">
          <Column field="row" header="Excel-sor"/>
          <Column field="upc" header="Cikkszám"/>
          <Column field="name" header="Megnevezés"/>
          <Column field="location" header="Tárhely"/>
          <Column header="Könyvi készlet" body={row => number(row.recorded)}/>
          <Column header="Foglalt" body={row => number(row.reserved)}/>
          <Column header="Bizonylaton" body={row => number(row.previous_quantity)}/>
          <Column header="Számolt" body={row => number(row.quantity)}/>
          <Column header="Eltérés" body={row => number(row.difference)}/>
          <Column header="Eredmény" body={row => row.errors.length ? <span className="p-error">{row.errors.join(' ')}</span> : row.note || actions[row.action]}/>
        </DataTable>
      </>}
    </Dialog>
  </div>;
}
